import type { Ref } from 'vue'
import type { Auction, CarrierBooking, Document, Message, Notification, Reference, TransportBatchUpdate, UserDetails, Workspace } from '@apiTypes'
import type { EmailNotification } from '@/types/email-notification'
import type { ObjectInteraction } from '@/types/object-interaction'

export enum PusherEventType {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}

type IdPushData = { id: number }
type IdHashPushData = { id: number; hash: string }

export type CurrencyRecalcPushData = Pick<Workspace, 'id' | 'currency' | 'currencyRecalcInProgress'>
export type MessagePushData = Message & { reference: Reference }
export type TimeSlotBookingPushData = Pick<CarrierBooking, 'id' | 'hash'> & { transport: number | null }

export enum PushEventName {
  Auction = 'auction',
  Transport = 'transport',
  TransportBatchUpdate = 'transportbatchupdate',
  EmailNotification = 'emailnotification',
  TimeSlotBooking = 'timeslotbooking',
  BookingLink = 'bookinglink',
  AddressTimeSlotsChanged = 'address:time_slots_changed',
  Carrier = 'carrier',
  Contact = 'contact',
  Contract = 'contract',
  Tender = 'tender',
  TenderOffersChanged = 'tender:offers_changed',
  TenderRoutesChanged = 'tender:routes_changed',
  TenderCategoriesChanged = 'tender:categories_changed',
  CurrencyRecalcStarted = 'workspace:currency_recalc:started',
  CurrencyRecalcFinished = 'workspace:currency_recalc:finished',
  CurrencyRecalcFailed = 'workspace:currency_recalc:failed',
  Message = 'message',
  ObjectInteraction = 'objectinteraction',
  Document = 'document',
  MessagesAllRead = 'messages_all_read',
  User = 'user',
  Notification = 'notification',
}

export interface PushEventMap {
  [PushEventName.Auction]: IdPushData
  [PushEventName.Transport]: IdHashPushData
  [PushEventName.TransportBatchUpdate]: TransportBatchUpdate
  [PushEventName.EmailNotification]: EmailNotification
  [PushEventName.TimeSlotBooking]: TimeSlotBookingPushData
  [PushEventName.BookingLink]: IdPushData
  [PushEventName.AddressTimeSlotsChanged]: IdPushData
  [PushEventName.Carrier]: IdPushData
  [PushEventName.Contact]: IdPushData
  [PushEventName.Contract]: IdHashPushData
  [PushEventName.Tender]: IdHashPushData
  [PushEventName.TenderOffersChanged]: IdHashPushData
  [PushEventName.TenderRoutesChanged]: IdHashPushData
  [PushEventName.TenderCategoriesChanged]: IdHashPushData
  [PushEventName.CurrencyRecalcStarted]: CurrencyRecalcPushData
  [PushEventName.CurrencyRecalcFinished]: CurrencyRecalcPushData
  [PushEventName.CurrencyRecalcFailed]: CurrencyRecalcPushData
  [PushEventName.Message]: MessagePushData
  [PushEventName.ObjectInteraction]: ObjectInteraction
  [PushEventName.Document]: Document
  [PushEventName.MessagesAllRead]: void
  [PushEventName.User]: UserDetails
  [PushEventName.Notification]: Notification
}

export type AuctionPushEventMap = Omit<PushEventMap, PushEventName.Auction> & { [PushEventName.Auction]: Pick<Auction, 'hash'> }

export type PushPayload<T> = { type: PusherEventType; data: T }
export type PushEventRef<T> = Ref<PushPayload<T> | undefined>
